import { Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { Button } from './ui/button';
import { X, Plus, Minus, ShoppingCart, Trash2, CreditCard } from 'lucide-react';

export default function CartSidebar() {
  const { 
    cartItems, 
    isCartOpen, 
    closeCart, 
    removeFromCart, 
    updateQuantity, 
    getCartTotal, 
    getCartCount,
    clearCart 
  } = useCart();

  if (!isCartOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm"
        onClick={closeCart}
      />
      
      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-zinc-900 z-50 shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <ShoppingCart className="text-red-500" size={24} />
            <h2 className="text-xl font-bold text-white">Mon Panier</h2>
            <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full">
              {getCartCount()}
            </span>
          </div>
          <button 
            onClick={closeCart}
            className="p-2 hover:bg-zinc-800 rounded-lg transition"
          >
            <X className="text-zinc-400" size={24} />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {cartItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingCart className="text-zinc-700 mb-4" size={64} />
              <p className="text-zinc-400 text-lg mb-2">Votre panier est vide</p>
              <p className="text-zinc-500 text-sm mb-6">Ajoutez des produits pour commencer</p>
              <Link to="/boutique" onClick={closeCart}>
                <Button className="bg-red-600 hover:bg-red-700">
                  Voir la boutique
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {cartItems.map((item) => (
                <div 
                  key={item.id}
                  className="bg-zinc-800/50 rounded-lg p-4 flex gap-4"
                >
                  {/* Image */}
                  <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-zinc-700">
                    {item.image ? (
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingCart className="text-zinc-500" size={24} />
                      </div>
                    )}
                  </div>
                  
                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold truncate">{item.name}</h3>
                    <p className="text-red-500 font-bold">{item.price.toFixed(2)}€</p>
                    
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-2 mt-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-1 bg-zinc-700 hover:bg-zinc-600 rounded transition"
                      >
                        <Minus size={14} className="text-white" />
                      </button>
                      <span className="text-white font-medium w-8 text-center">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-1 bg-zinc-700 hover:bg-zinc-600 rounded transition"
                      >
                        <Plus size={14} className="text-white" />
                      </button>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="p-1 ml-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  
                  {/* Subtotal */}
                  <div className="text-right">
                    <p className="text-zinc-400 text-xs">Sous-total</p>
                    <p className="text-white font-bold">
                      {(item.price * item.quantity).toFixed(2)}€
                    </p>
                  </div>
                </div>
              ))}
              
              {/* Clear Cart Button */}
              <button
                onClick={clearCart}
                className="w-full py-2 text-zinc-500 hover:text-red-400 text-sm flex items-center justify-center gap-2 transition"
              >
                <Trash2 size={14} />
                Vider le panier
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        {cartItems.length > 0 && (
          <div className="border-t border-zinc-800 p-4 space-y-4">
            {/* Total */}
            <div className="flex justify-between items-center">
              <span className="text-zinc-400">Total</span>
              <span className="text-2xl font-bold text-white">
                {getCartTotal().toFixed(2)}€
              </span>
            </div>
            
            {/* Checkout Button */}
            <Link to="/checkout" onClick={closeCart}>
              <Button className="w-full bg-red-600 hover:bg-red-700 text-lg py-6 btn-glow">
                <CreditCard size={20} className="mr-2" />
                Passer commande
              </Button>
            </Link>
            
            {/* Continue Shopping */}
            <button
              onClick={closeCart}
              className="w-full py-2 text-zinc-400 hover:text-white text-sm transition"
            >
              Continuer mes achats
            </button>
          </div>
        )}
      </div>
    </>
  );
}
